export type FeatureStatus =
  | 'PRODUCTION_READY'
  | 'PARTIAL'
  | 'SIMULATION'
  | 'DEMO_ONLY'
  | 'ADMIN_ONLY'
  | 'LEGACY';

const statusLabels: Record<FeatureStatus, string> = {
  PRODUCTION_READY: 'Production Ready',
  PARTIAL: 'Draft UX',
  SIMULATION: 'Simulation Mode',
  DEMO_ONLY: 'Demo Only',
  ADMIN_ONLY: 'Advanced Admin',
  LEGACY: 'Legacy',
};

const statusDescriptions: Record<FeatureStatus, string> = {
  PRODUCTION_READY: 'Connected to backend persistence and runtime APIs.',
  PARTIAL: 'Connected where backend contracts exist; unfinished parts are explicitly marked.',
  SIMULATION: 'No external delivery is performed. Outputs are generated for verification only.',
  DEMO_ONLY: 'Uses fixed demonstration data and must not be treated as persisted production setup.',
  ADMIN_ONLY: 'Raw administrative surface for platform teams and advanced support.',
  LEGACY: 'Preserved for compatibility with existing routes and operations.',
};

export function FeatureStatusBadge({ status }: { status: FeatureStatus }) {
  return <span className={`feature-badge feature-${status.toLowerCase()}`}>{statusLabels[status]}</span>;
}

export function BackendIndicator({ connected }: { connected: boolean }) {
  return (
    <span className={`backend-indicator ${connected ? 'connected' : 'simulation'}`}>
      {connected ? 'Backend connected' : 'Local simulation'}
    </span>
  );
}

export function TrustBoundaryNotice({
  status,
  connected,
  detail,
}: {
  status: FeatureStatus;
  connected: boolean;
  detail?: string;
}) {
  return (
    <div className={`trust-boundary trust-${status.toLowerCase()}`}>
      <div>
        <FeatureStatusBadge status={status} />
        <BackendIndicator connected={connected} />
      </div>
      <p>{detail ?? statusDescriptions[status]}</p>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  purpose,
  status,
  connected = true,
  next,
  trustDetail,
}: {
  eyebrow?: string;
  title: string;
  purpose: string;
  status: FeatureStatus;
  connected?: boolean;
  next?: string;
  trustDetail?: string;
}) {
  return (
    <section className="page-header">
      <div>
        {eyebrow ? <span>{eyebrow}</span> : null}
        <h1>{title}</h1>
        <p>{purpose}</p>
        {next ? <p className="next-guidance"><strong>Next:</strong> {next}</p> : null}
      </div>
      <TrustBoundaryNotice status={status} connected={connected} detail={trustDetail} />
    </section>
  );
}
